// src/components/HabitTracker.jsx (TRANSLATED)
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  setDoc,
  getDocs,
  increment,
  deleteDoc,
  deleteField,
} from "firebase/firestore";
import {
  format,
  addDays,
  subDays,
  isToday,
  startOfDay,
  startOfWeek,
  isAfter,
  isSameDay,
} from "date-fns";
import {
  FaDumbbell,
  FaBook,
  FaBed,
  FaBrain,
  FaHeart,
  FaEdit,
} from "react-icons/fa";
import { FaTrashCan } from "react-icons/fa6";
import noti from "../assets/notiSound.mp3";
import { PACKAGES } from "../utils/entitlements";
import { useTranslation } from "react-i18next"; // Added import

const icons = [FaDumbbell, FaBook, FaBed, FaBrain, FaHeart];

// --- QUOTE CONFIGURATION (Must match Settings.jsx) ---
const INITIAL_QUOTES = {
  0: "Let's conquer the day! Hard things first💪",
  1: "Good Start - Keep it going",
  40: "Woohoo! — Keep the momentum going!",
  70: "Almost there, stay consistent!",
  90: "Almost done — finish strong!",
  100: "Mission Accomplished! Enjoy your day🫡",
};

// ---------------- STYLES ----------------
const Container = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 2.5rem;
  border-radius: 14px;
  /* width: 33%; */
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  transition: 0.5s;
  color: var(--fontPrimaryColor);
  position: relative;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  height: stretch;
  max-height: 600px;
  min-height: 400px;
  @media (max-height: 1100px) {
    max-height: 500px;
  }
  /* @media (max-width: 1400px) {
    width: 48%;
  }
  @media (max-width: 950px) {
    width: 100%;
  }*/
  @media (max-width: 500px) {
    padding: 2em 1.2em;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Title = styled.h2`
  font-size: 1.2rem;
  color: var(--fontPrimaryColor);
  font-weight: 500;
`;

const DateNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  strong {
    font-weight: 400;
  }
  @media (max-width: 500px) {
    font-size: 0.8rem;
  }
`;

const NavButton = styled.button`
  background: #495269;
  border: none;
  padding: 0.3rem 0.6rem 0.7em 0.6em;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: 0.2s;
  color: white;
  &:hover {
    background: #31578a;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  @media (max-width: 500px) {
    font-size: 0.8rem;
  }
`;

const ProgressBarContainer = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 8px;
  height: 22px;
  overflow: hidden;
  transition: 0.5s;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${({ width }) => width}%;
  transition: width 0.3s ease, background 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: ${({ width }) => (width > 10 ? "flex-end" : "flex-start")};
  padding: 0 0.5rem;
  color: white;
  font-weight: 400;
  transition: 0.5s;
  background: ${({ width }) => {
    if (width < 30) return "#f87171";
    if (width < 70) return "#facc15";
    if (width === 100) return "#23720b";
    return "#22c55e";
  }};
`;

const MotivationText = styled.p`
  text-align: center;
  /* margin-top: 0.25rem; */
  color: var(--fontSecondaryColor);
  font-weight: 400;
  @media (max-width: 500px) {
    font-size: 0.9em;
  }
`;

const HabitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  max-height: 200px;
  overflow-y: scroll;
  @media (max-width: 500px) {
    font-size: 0.9em;
  }
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #717d9b;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background-color: var(--secondaryBackgroundColor);
  }
`;

const HabitItem = styled.div`
  background: var(--thirdBackgroundColor);
  padding: 0.9rem;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: ${({ completed }) => (completed ? "line-through" : "none")};
  background: ${({ isPushed, completed, failed }) => {
    if (isPushed) return "#e0f2fe";
    if (failed) return "#fee2e2";
    if (completed) return "var(--completedItem)";
    return "var(--thirdBackgroundColor)";
  }};
  color: ${({ isPushed, completed, failed, futureView }) =>
    isPushed
      ? "#1e40af"
      : failed
      ? "var(--fontPrimaryColor)"
      : completed
      ? "var(--completedItemTxt)"
      : futureView
      ? "#9ca3af"
      : "var(--fontPrimaryColor)"};
  transition: 0.5s;
  cursor: ${({ futureView }) => (futureView ? "not-allowed" : "pointer")};
  opacity: ${({ futureView }) => (futureView ? 0.75 : 1)};
  filter: ${({ futureView }) => (futureView ? "grayscale(10%)" : "none")};
  border-left: 5px solid
    ${({ isPushed, completed, failed }) => {
      if (isPushed) return "#3b82f6";
      if (failed) return "#ef4444";
      if (completed) return "#10b981";
      return "transparent";
    }};
  &:hover {
    background: ${({ isPushed, completed, failed, futureView }) => {
      if (futureView) return "var(--thirdBackgroundColor)";
      if (isPushed) return "#1c71da";
      if (failed) return "#fecaca";
      if (completed) return "var(--completedItem)";
      return "var(--forthBackgroundColor)";
    }};
  }
  @media (max-width: 500px) {
    span {
      font-size: 14px;
    }
  }
`;

const HabitInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const HabitActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 10px;
`;

const ActionIcon = styled.span`
  cursor: pointer;
  font-size: 1rem;
  color: var(--fontSecondaryColor);
  transition: color 0.2s;
  &:hover {
    color: #38bdf8;
    & .trash-icon {
      color: #ef4444;
    }
  }
  & .trash-icon {
    font-size: 0.9rem;
  }
`;

const AddButton = styled.button`
  margin-top: 0.3rem;
  background: #495269;
  color: white;
  border: none;
  width: 100%;
  padding: 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: 0.2s;
  margin-bottom: 1em;
  font-family: "Montserrat", sans-serif;
  &:hover {
    background: #717d9b;
  }
`;

const Empty = styled.p`
  opacity: 0.6;
  text-align: center;
  padding: 1rem 0;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  backdrop-filter: blur(10px) brightness(0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Popup = styled.div`
  background: var(--popupColor);
  padding: 1.6rem;
  width: 500px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  select {
    padding: 15px 20px;
    border-radius: 5px;
    border: none;
    font-family: "Montserrat", sans-serif;
    font-size: 0.9rem;
    background: var(--thirdBackgroundColor);
    outline: none;
    color: var(--fontPrimaryColor);
    box-shadow: 0 0 10px 1px var(--popupShadow);
    cursor: pointer;
    option {
      padding: 15px 20px;
      height: 100px;
    }
  }
  @media (max-width: 550px) {
    width: 90%;
  }
`;

const Label = styled.p`
  opacity: 0.7;
  font-size: 0.9rem;
  border: none;
`;

const IconSelect = styled.div`
  display: flex;
  padding: 0;
  gap: 10px;
  @media (max-width: 550px) {
    gap: 0;
  }
`;

const IconButton = styled.button`
  background: ${({ active }) =>
    active ? "#35a335" : "var(--thirdBackgroundColor)"};
  color: ${({ active }) => (active ? "#fff" : "var(--fontPrimaryColor)")};
  border-radius: 8px;
  font-size: 1.6em;
  border: none;
  cursor: pointer;
  padding: 0.3em 0.7em 0.1em 0.7em;
  @media (max-width: 550px) {
    padding: 0.3em 0.5em 0.1em 0.5em;
  }
`;

const Days = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Day = styled.button`
  padding: 0.7rem 1rem;
  border-radius: 6px;
  background: ${({ active }) =>
    active ? "#35a335" : "var(--thirdBackgroundColor)"};
  color: ${({ active }) => (active ? "#fff" : "var(--fontPrimaryColor)")};
  box-shadow: 0 0 10px 1px var(--popupShadow);
  border: none;
  cursor: pointer;
  font-family: "Montserrat", sans-serif;
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  padding-bottom: 4px;
  border-bottom: 2px solid rgba(34, 197, 94, 0.18);
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  background: ${({ filled }) => (filled ? "#22c55e" : "#cbd5e1")};
`;

const PopupButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  & button {
    padding: 0.6rem 1rem;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-weight: 500;
  }
  & button:first-child {
    background: var(--thirdBackgroundColor);
    color: var(--fontPrimaryColor);
  }
  & button:last-child {
    background: var(--fontPrimaryColor);
    color: var(--thirdBackgroundColor);
  }
`;

const Input = styled.input`
  padding: 15px 20px;
  border-radius: 5px;
  border: none;
  font-family: "Montserrat", sans-serif;
  font-size: 0.9rem;
  background: var(--thirdBackgroundColor);
  outline: none;
  color: var(--fontPrimaryColor);
  box-shadow: 0 0 10px 1px var(--popupShadow);
  &::placeholder {
    color: var(--fontSecondaryColor);
  }
`;

const DetailsLink = styled.a`
  position: absolute;
  bottom: 25px;
  right: 25px;
  font-size: 0.85rem;
  color: var(--fontSecondaryColor);
  text-decoration: underline;
  cursor: pointer;
  padding-top: 10px;
  z-index: 10;
  transition: color 0.2s;
  &:hover {
    color: #38bdf8;
  }
`;

const ConfirmationMessage = styled.p`
  text-align: center;
  font-size: 1rem;
  color: var(--fontPrimaryColor);
`;

const TaskToConfirm = styled.span`
  font-weight: bold;
  color: #ef4444;
`;

// ---------------- Component ----------------
export default function HabitTracker() {
  const { userId, subscriptionTier } = useAuth();
  const isFullOption = subscriptionTier === PACKAGES.FULL_OPTION;
  const { t, i18n } = useTranslation(); // Added translation hook

  const [customQuotes, setCustomQuotes] = useState(INITIAL_QUOTES);
  const [habits, setHabits] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [newName, setNewName] = useState("");
  const [selectedIcon, setSelectedIcon] = useState(FaDumbbell);
  const [days, setDays] = useState([]);
  const [recurrenceType, setRecurrenceType] = useState("SPECIFIC_DAYS");
  const [targetCount, setTargetCount] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [isEditing, setIsEditing] = useState(false);
  const [editHabitData, setEditHabitData] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [habitToDelete, setHabitToDelete] = useState(null);

  const dateKey = format(selectedDate, "yyyy-MM-dd");
  const selectedDay = format(selectedDate, "EEEE");
  const isFutureView = isAfter(
    startOfDay(selectedDate),
    startOfDay(new Date())
  );

  // Use translated week days
  const weekDays = [
    t("days.monday"),
    t("days.tuesday"),
    t("days.wednesday"),
    t("days.thursday"),
    t("days.friday"),
    t("days.saturday"),
    t("days.sunday"),
  ];

  const weekDaysShort = [
    t("days.mondayShort"),
    t("days.tuesdayShort"),
    t("days.wednesdayShort"),
    t("days.thursdayShort"),
    t("days.fridayShort"),
    t("days.saturdayShort"),
    t("days.sundayShort"),
  ];

  const playHabitCompleteSound = () => {
    const audio = new Audio(noti);
    audio.volume = 0.9;
    audio.play().catch(() => {});
  };

  const getUserCollection = (collectionName) => {
    if (!userId) return null;
    return collection(db, "users", userId, collectionName);
  };

  const getUserHabitDoc = (habitId) => {
    if (!userId || !habitId) return null;
    return doc(db, "users", userId, "habits", habitId);
  };

  const getUserHabitProgressDoc = (dateKey) => {
    if (!userId || !dateKey) return null;
    return doc(db, "users", userId, "habitProgress", dateKey);
  };

  const getSettingsDocRef = () => {
    if (!userId) return null;
    return doc(db, "users", userId, "settings", "habitTracker");
  };

  const updateDailyProgress = async (date) => {
    const dateKeyForUpdate = format(date, "yyyy-MM-dd");
    const weekdayName = format(date, "EEEE");
    const weekKey = format(
      startOfWeek(date, { weekStartsOn: 1 }),
      "yyyy-MM-dd"
    );

    // Use the existing habits state
    const dayHabitsForDate = habits.filter((h) => {
      const habitStartDate = h.startDate?.toDate
        ? h.startDate.toDate()
        : new Date(0);
      if (isAfter(habitStartDate, date) && !isSameDay(habitStartDate, date)) {
        return false;
      }
      const type =
        h.recurrenceType ||
        (h.days && h.days.length ? "SPECIFIC_DAYS" : "DAILY");
      if (type === "FLEXIBLE_WEEKLY") return true;
      const hdays = h.days || [];
      if (!hdays || hdays.length === 0) return true;
      return hdays.includes(weekdayName);
    });

    const totalHabits = dayHabitsForDate.length;
    let completedCount = 0;

    dayHabitsForDate.forEach((h) => {
      // Only count as completed if actually completed on this specific date
      const status =
        h.statusByDate?.[dateKeyForUpdate] ||
        (h.completedDates?.[dateKeyForUpdate] ? "completed" : null);
      if (status === "completed") {
        completedCount += 1;
      }
    });

    const percentage = totalHabits
      ? Math.round((completedCount / totalHabits) * 100)
      : 0;

    if (!userId) return;
    const progressDoc = getUserHabitProgressDoc(dateKeyForUpdate);
    if (!progressDoc) return;

    try {
      await setDoc(progressDoc, {
        date: startOfDay(date),
        percentage,
      });
    } catch (err) {
      console.error("Error updating daily progress:", err);
    }
  };

  // 1. Fetch habits in real-time
  useEffect(() => {
    if (!userId) return;
    const col = getUserCollection("habits");
    if (!col) return;
    const unsub = onSnapshot(col, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setHabits(data);
    });
    return () => unsub();
  }, [userId]);

  // 2. Fetch custom quotes
  useEffect(() => {
    if (!userId || !isFullOption) {
      setCustomQuotes(INITIAL_QUOTES);
      return;
    }
    const settingsRef = getSettingsDocRef();
    if (!settingsRef) return;

    const unsub = onSnapshot(
      settingsRef,
      (docSnap) => {
        if (docSnap.exists() && docSnap.data().progressQuotes) {
          const fetchedQuotes = docSnap.data().progressQuotes;
          const safeFetched = Object.fromEntries(
            Object.entries(fetchedQuotes).map(([key, value]) => [
              String(key),
              value,
            ])
          );
          setCustomQuotes({
            ...INITIAL_QUOTES,
            ...safeFetched,
          });
        } else {
          setCustomQuotes(INITIAL_QUOTES);
        }
      },
      (error) => {
        console.error("Error fetching live custom quotes:", error);
        setCustomQuotes(INITIAL_QUOTES);
      }
    );
    return () => unsub();
  }, [userId, isFullOption]);

  // 3. Update progress when habits change
  useEffect(() => {
    if (userId && habits.length > 0) {
      updateDailyProgress(new Date());
    }
  }, [userId, habits]);

  useEffect(() => {
    if (userId) {
      updateDailyProgress(selectedDate);
    }
  }, [selectedDate, userId, habits]);

  // Filter habits for selected date
  const weekKeyForSelected = format(
    startOfWeek(selectedDate, { weekStartsOn: 1 }),
    "yyyy-MM-dd"
  );

  const dayHabits = habits.filter((h) => {
    const habitStartDate = h.startDate?.toDate
      ? h.startDate.toDate()
      : new Date(0);
    if (
      isAfter(habitStartDate, selectedDate) &&
      !isSameDay(habitStartDate, selectedDate)
    ) {
      return false;
    }
    const type =
      h.recurrenceType || (h.days && h.days.length ? "SPECIFIC_DAYS" : "DAILY");
    if (type === "FLEXIBLE_WEEKLY") return true;
    const hdays = h.days || [];
    if (!hdays || hdays.length === 0) return true;
    return hdays.includes(selectedDay);
  });

  // Calculate progress
  const totalHabitsForSelected = dayHabits.length;
  const completedForSelected = dayHabits.reduce((sum, h) => {
    // Only count as completed if actually completed on this specific date
    if (getHabitStatusForDate(h, dateKey) === "completed") return sum + 1;
    return sum;
  }, 0);

  const progress = totalHabitsForSelected
    ? Math.round((completedForSelected / totalHabitsForSelected) * 100)
    : 0;

  const getMotivation = () => {
    // Check if the quote is a default quote
    const isDefaultQuote = (quote) => {
      return Object.values(INITIAL_QUOTES).includes(quote);
    };

    // Get quote based on progress
    let quote;
    if (progress === 0) quote = customQuotes["0"] || INITIAL_QUOTES["0"];
    else if (progress === 100)
      quote = customQuotes["100"] || INITIAL_QUOTES["100"];
    else if (progress >= 90) quote = customQuotes["90"] || INITIAL_QUOTES["90"];
    else if (progress >= 70) quote = customQuotes["70"] || INITIAL_QUOTES["70"];
    else if (progress >= 40) quote = customQuotes["40"] || INITIAL_QUOTES["40"];
    else quote = customQuotes["1"] || INITIAL_QUOTES["1"];

    // If it's a default quote, use translation, otherwise keep custom quote as is
    if (isDefaultQuote(quote)) {
      // Return translated quote based on progress
      if (progress === 0) return t("habits.quotes.0");
      if (progress === 100) return t("habits.quotes.100");
      if (progress >= 90) return t("habits.quotes.90");
      if (progress >= 70) return t("habits.quotes.70");
      if (progress >= 40) return t("habits.quotes.40");
      return t("habits.quotes.1");
    } else {
      // Return custom quote as-is (no translation)
      return quote;
    }
  };

  const resetPopupState = () => {
    setShowPopup(false);
    setIsEditing(false);
    setEditHabitData(null);
    setNewName("");
    setDays([]);
    setSelectedIcon(FaDumbbell);
    setRecurrenceType("SPECIFIC_DAYS");
    setTargetCount(1);
  };

  function getHabitStatusForDate(habit, key) {
    if (!habit || !key) return null;
    const status = habit.statusByDate?.[key];
    if (status === "completed" || status === "failed") return status;
    // Backward compatibility for older docs that only have completedDates
    return habit.completedDates?.[key] ? "completed" : null;
  }

  // Cycle habit status: neutral -> completed -> failed -> neutral
  const toggleHabit = async (habit) => {
    if (!userId || isFutureView) return;

    const habitRef = getUserHabitDoc(habit.id);
    if (!habitRef) return;

    const currentStatus = getHabitStatusForDate(habit, dateKey);
    const nextStatus =
      currentStatus === "completed"
        ? "failed"
        : currentStatus === "failed"
        ? null
        : "completed";

    const wasCompleted = currentStatus === "completed";
    const willBeCompleted = nextStatus === "completed";

    if (!wasCompleted && willBeCompleted) {
      playHabitCompleteSound();
    }

    const type =
      habit.recurrenceType ||
      (habit.days && habit.days.length ? "SPECIFIC_DAYS" : "DAILY");

    if (type === "FLEXIBLE_WEEKLY") {
      const weekKey = format(
        startOfWeek(selectedDate, { weekStartsOn: 1 }),
        "yyyy-MM-dd"
      );
      try {
        const currentCount = habit.weeklyLogs?.[weekKey] || 0;
        const weeklyDelta =
          !wasCompleted && willBeCompleted
            ? 1
            : wasCompleted && !willBeCompleted && currentCount > 0
            ? -1
            : 0;

        const updates = {};
        if (nextStatus) {
          updates[`statusByDate.${dateKey}`] = nextStatus;
          updates[`completedDates.${dateKey}`] = willBeCompleted;
        } else {
          updates[`statusByDate.${dateKey}`] = deleteField();
          updates[`completedDates.${dateKey}`] = deleteField();
        }

        if (weeklyDelta !== 0) {
          updates[`weeklyLogs.${weekKey}`] = increment(weeklyDelta);
        }

        await updateDoc(habitRef, updates);
      } catch (err) {
        console.error("Error toggling flexible weekly habit:", err);
      }
    } else {
      try {
        const updates = {};
        if (nextStatus) {
          updates[`statusByDate.${dateKey}`] = nextStatus;
          updates[`completedDates.${dateKey}`] = willBeCompleted;
        } else {
          updates[`statusByDate.${dateKey}`] = deleteField();
          updates[`completedDates.${dateKey}`] = deleteField();
        }

        await updateDoc(habitRef, updates);
      } catch (err) {
        console.error("Error toggling habit:", err);
      }
    }

    await updateDailyProgress(selectedDate);
  };

  const toggleDay = (day) =>
    setDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );

  const addHabit = async () => {
    if (!newName.trim()) return;
    if (recurrenceType === "SPECIFIC_DAYS" && days.length === 0) return;
    if (
      recurrenceType === "FLEXIBLE_WEEKLY" &&
      (!targetCount || targetCount < 1 || targetCount > 7)
    )
      return;

    if (!userId) return;
    const col = getUserCollection("habits");
    if (!col) return;

    const newHabit = {
      name: newName,
      icon: selectedIcon.name,
      days: recurrenceType === "SPECIFIC_DAYS" ? days : [],
      completedDates: {},
      recurrenceType,
      startDate: new Date(),
    };

    if (recurrenceType === "FLEXIBLE_WEEKLY") {
      newHabit.targetCount = targetCount;
      newHabit.weeklyLogs = {};
    }

    try {
      await addDoc(col, newHabit);
      resetPopupState();

      // Update progress immediately
      await updateDailyProgress(new Date());
      await updateDailyProgress(selectedDate);
    } catch (err) {
      console.error("Error adding habit:", err);
    }
  };

  const handleEdit = (e, habit) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditHabitData(habit);
    setNewName(habit.name);
    setSelectedIcon(icons.find((i) => i.name === habit.icon) || FaDumbbell);
    setDays(habit.days || []);
    setRecurrenceType(habit.recurrenceType || "DAILY");
    setTargetCount(habit.targetCount || 1);
    setShowPopup(true);
  };

  const updateHabit = async () => {
    if (!newName.trim() || !editHabitData) return;
    if (recurrenceType === "SPECIFIC_DAYS" && days.length === 0) return;
    if (
      recurrenceType === "FLEXIBLE_WEEKLY" &&
      (!targetCount || targetCount < 1 || targetCount > 7)
    )
      return;

    const habitRef = getUserHabitDoc(editHabitData.id);
    if (!habitRef) return;

    const updates = {
      name: newName,
      icon: selectedIcon.name,
      recurrenceType,
      targetCount: 1,
      days: [],
    };

    if (recurrenceType === "SPECIFIC_DAYS") {
      updates.days = days;
    } else if (recurrenceType === "FLEXIBLE_WEEKLY") {
      updates.targetCount = targetCount;
    }

    try {
      await updateDoc(habitRef, updates);
      resetPopupState();
      await updateDailyProgress(new Date());
    } catch (err) {
      console.error("Error updating habit:", err);
    }
  };

  const handleDelete = (e, habit) => {
    e.stopPropagation();
    setHabitToDelete(habit);
    setShowConfirmPopup(true);
  };

  const confirmDelete = async () => {
    if (!habitToDelete) return;
    try {
      const habitRef = getUserHabitDoc(habitToDelete.id);
      if (habitRef) await deleteDoc(habitRef);
      setShowConfirmPopup(false);
      setHabitToDelete(null);
      await updateDailyProgress(new Date());
      await updateDailyProgress(selectedDate);
    } catch (error) {
      console.error("Error deleting habit:", error);
    }
  };

  // Format date with translation support
  const formatTranslatedDate = (date) => {
    const dayDiff = differenceInCalendarDays(
      startOfDay(date),
      startOfDay(new Date())
    );
    const isSelectedToday = isToday(date);

    if (isSelectedToday) return t("habits.today");
    if (dayDiff === 1) return t("habits.tomorrow");

    return format(date, "EEEE, MMM d");
  };

  return (
    <Container>
      <Header>
        <Title>{t("habits.title")}</Title>
        <DateNav>
          <NavButton onClick={() => setSelectedDate((d) => subDays(d, 1))}>
            ←
          </NavButton>
          <strong>{formatTranslatedDate(selectedDate)}</strong>
          <NavButton
            disabled={isToday(selectedDate)}
            onClick={() => setSelectedDate((d) => addDays(d, 1))}
          >
            →
          </NavButton>
        </DateNav>
      </Header>

      <div
        style={{
          textAlign: "center",
          marginBottom: "0.5rem",
          marginTop: "10px",
        }}
      >
        <strong>{progress}%</strong>
      </div>

      {habits.length > 0 && (
        <>
          <ProgressBarContainer>
            <ProgressBarFill width={progress}>{progress}%</ProgressBarFill>
          </ProgressBarContainer>

          <MotivationText>{getMotivation()}</MotivationText>
        </>
      )}

      <HabitsList>
        {dayHabits.length === 0 && <Empty>{t("habits.noHabits")}</Empty>}
        {dayHabits.map((h) => {
          const Icon = icons.find((i) => i.name === h.icon) || FaDumbbell;
          const type =
            h.recurrenceType ||
            (h.days && h.days.length ? "SPECIFIC_DAYS" : "DAILY");
          const status = getHabitStatusForDate(h, dateKey);
          const done = status === "completed";
          const failed = status === "failed";
          const currentCount =
            type === "FLEXIBLE_WEEKLY"
              ? h.weeklyLogs?.[weekKeyForSelected] || 0
              : null;
          const target = h.targetCount || 1;
          const isTargetMet =
            type === "FLEXIBLE_WEEKLY" && currentCount >= target;
          const extraCount = Math.max(0, currentCount - target);
          const isViewPastOrFuture = isFutureView || !isToday(selectedDate);

          const isCompleted = done;
          const showTargetMetIndicator = isTargetMet && !done;

          return (
            <HabitItem
              key={h.id}
              completed={isCompleted}
              failed={failed}
              futureView={isFutureView}
              onClick={() => !isFutureView && toggleHabit(h)}
            >
              <HabitInfo>
                <Icon />
                <span style={{ flex: 1 }}>{h.name}</span>
              </HabitInfo>

              {type === "FLEXIBLE_WEEKLY" && (
                <Dots aria-hidden="true">
                  {/* Target dots */}
                  {Array.from({ length: target }).map((_, i) => (
                    <Dot
                      key={i}
                      filled={i < (currentCount || 0)}
                      title={`${
                        i < currentCount
                          ? t("habits.completed")
                          : t("habits.pending")
                      } (${t("habits.target")})`}
                    />
                  ))}

                  {/* Check icon when target is met but not completed today */}
                  {showTargetMetIndicator && (
                    <span
                      style={{
                        marginLeft: "6px",
                        color: "#10b981",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                      }}
                      title={t("habits.targetMet")}
                    >
                      ✓
                    </span>
                  )}

                  {/* Extra completions counter */}
                  {extraCount > 0 && (
                    <span
                      style={{
                        marginLeft: "6px",
                        fontSize: "0.9rem",
                        color: "#22c55e",
                        fontWeight: "bold",
                        textDecoration: "none",
                      }}
                      title={t("habits.extraCompletions", {
                        count: extraCount,
                      })}
                    >
                      +{extraCount}
                    </span>
                  )}
                </Dots>
              )}

              <HabitActions onClick={(e) => e.stopPropagation()}>
                {!isViewPastOrFuture && (
                  <>
                    <ActionIcon
                      onClick={(e) => handleEdit(e, h)}
                      title={t("habits.editHabit")}
                    >
                      <FaEdit />
                    </ActionIcon>
                    <ActionIcon
                      onClick={(e) => handleDelete(e, h)}
                      title={t("habits.delete")}
                    >
                      <FaTrashCan className="trash-icon" />
                    </ActionIcon>
                  </>
                )}
              </HabitActions>
            </HabitItem>
          );
        })}
      </HabitsList>

      <AddButton onClick={() => setShowPopup(true)}>
        {t("habits.addHabit")}
      </AddButton>

      {/* Add/Edit Habit Popup */}
      {showPopup && (
        <Overlay onClick={resetPopupState}>
          <Popup onClick={(e) => e.stopPropagation()}>
            <h3>{isEditing ? t("habits.editHabit") : t("habits.addHabit")}</h3>
            <Input
              placeholder={t("habits.habitPlaceholder")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <Label>{t("habits.recurrenceType")}</Label>
            <select
              value={recurrenceType}
              onChange={(e) => setRecurrenceType(e.target.value)}
            >
              <option value="SPECIFIC_DAYS">{t("habits.specificDays")}</option>
              <option value="DAILY">{t("habits.everyDay")}</option>
              <option value="FLEXIBLE_WEEKLY">
                {t("habits.flexibleWeekly")}
              </option>
            </select>

            {recurrenceType === "SPECIFIC_DAYS" && (
              <>
                <Label>{t("habits.whichDays")}</Label>
                <Days>
                  {weekDays.map((d, index) => (
                    <Day
                      key={d}
                      active={days.includes(d)}
                      onClick={() => toggleDay(d)}
                    >
                      {weekDaysShort[index]}
                    </Day>
                  ))}
                </Days>
              </>
            )}

            {recurrenceType === "FLEXIBLE_WEEKLY" && (
              <div>
                <Label>{t("habits.targetPerWeek")}</Label>
                <Input
                  type="number"
                  min={1}
                  max={7}
                  value={targetCount}
                  onChange={(e) => setTargetCount(Number(e.target.value))}
                />
              </div>
            )}

            <Label>{t("habits.chooseIcon")}</Label>
            <IconSelect>
              {icons.map((Icon, i) => (
                <IconButton
                  key={i}
                  active={selectedIcon.name === Icon.name}
                  onClick={() => setSelectedIcon(() => Icon)}
                >
                  <Icon />
                </IconButton>
              ))}
            </IconSelect>

            <PopupButtons>
              <button onClick={resetPopupState}>{t("habits.cancel")}</button>
              <button
                onClick={isEditing ? updateHabit : addHabit}
                disabled={
                  !newName.trim() ||
                  (recurrenceType === "SPECIFIC_DAYS" && days.length === 0) ||
                  (recurrenceType === "FLEXIBLE_WEEKLY" &&
                    (!targetCount || targetCount < 1 || targetCount > 7))
                }
              >
                {isEditing ? t("habits.saveChanges") : t("habits.add")}
              </button>
            </PopupButtons>
          </Popup>
        </Overlay>
      )}

      {/* Confirmation Popup for Deletion */}
      {showConfirmPopup && habitToDelete && (
        <Overlay onClick={() => setShowConfirmPopup(false)}>
          <Popup onClick={(e) => e.stopPropagation()}>
            <h3>{t("habits.confirmDeletion")}</h3>
            <ConfirmationMessage>
              {t("habits.deleteConfirmation")}{" "}
              <TaskToConfirm>"{habitToDelete.name}"</TaskToConfirm>?
            </ConfirmationMessage>
            <PopupButtons>
              <button onClick={() => setShowConfirmPopup(false)}>
                {t("habits.cancel")}
              </button>
              <button
                onClick={confirmDelete}
                style={{ background: "#ef4444", color: "white" }}
              >
                {t("habits.delete")}
              </button>
            </PopupButtons>
          </Popup>
        </Overlay>
      )}

      <DetailsLink
        href="/habit-analysis"
        onClick={() => console.log("Navigating to detailed habit history...")}
      >
        {t("habits.moreDetails")}
      </DetailsLink>
    </Container>
  );
}

// Helper function to calculate day difference
function differenceInCalendarDays(dateLeft, dateRight) {
  const startOfDayLeft = startOfDay(dateLeft);
  const startOfDayRight = startOfDay(dateRight);
  const diffTime = startOfDayLeft.getTime() - startOfDayRight.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}
